import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { legacyOrdersApi } from '@/api/legacy';
import type { CreateOrderDto, UpdateOrderDto } from '@/api/legacy';

/**
 * Get orders for a specific client
 */
export function useClientOrders(clientName: string, archive: 0 | 1 = 0) {
  return useQuery({
    queryKey: ['legacy', 'orders', clientName, archive],
    queryFn: () => legacyOrdersApi.getByClient(clientName, archive),
    enabled: !!clientName,
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Create order mutation
 */
export function useCreateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateOrderDto) => legacyOrdersApi.create(dto),
    onSuccess: (_, variables) => {
      // Invalidate both active and archived orders for this client
      queryClient.invalidateQueries({
        queryKey: ['legacy', 'orders', variables.company, 0],
      });
      queryClient.invalidateQueries({
        queryKey: ['legacy', 'orders', variables.company, 1],
      });
      toast.success('Заказ создан');
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка создания заказа';
      toast.error('Ошибка', { description: message });
    },
  });
}

/**
 * Update order mutation
 */
export function useUpdateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateOrderDto }) =>
      legacyOrdersApi.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legacy', 'orders'] });
      toast.success('Заказ обновлён');
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка обновления заказа';
      toast.error('Ошибка', { description: message });
    },
  });
}

/**
 * Archive order mutation
 */
export function useArchiveOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => legacyOrdersApi.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legacy', 'orders'] });
      toast.success('Заказ архивирован');
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка архивации заказа';
      toast.error('Ошибка', { description: message });
    },
  });
}

/**
 * Request invoice refresh mutation
 */
export function useRefreshSchet() {
  return useMutation({
    mutationFn: (id: number) => legacyOrdersApi.refreshSchet(id),
    onSuccess: () => {
      toast.success('Запрос отправлен', {
        description: 'Менеджер обновит счёт',
      });
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка отправки запроса';
      toast.error('Ошибка', { description: message });
    },
  });
}

/**
 * Add comment to order mutation
 */
export function useAddOrderComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comment }: { id: number; comment: string }) =>
      legacyOrdersApi.addComment(id, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legacy', 'orders'] });
      toast.success('Уточнение отправлено');
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка отправки уточнения';
      toast.error('Ошибка', { description: message });
    },
  });
}

/**
 * Send warranty certificate mutation
 */
export function useSendGuarantee() {
  return useMutation({
    mutationFn: ({ id, email }: { id: number; email: string }) =>
      legacyOrdersApi.sendGuarantee(id, email),
    onSuccess: () => {
      toast.success('Гарантийный талон отправлен');
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка отправки гарантийки';
      toast.error('Ошибка', { description: message });
    },
  });
}
